/**
 * 聊天 API 服务 v4
 * 方案B：无快捷按钮，纯自然语言
 */
export interface OpenResponse {
  openingMessage: string;
  campDay: number;
  theme: string;
  stage: string;
  stageName: string;
  hasFormula: boolean;
  completedCheckins?: string[];
  actions?: ChatAction[];
  history?: ChatHistoryMessage[];
  resumed?: boolean;
}

export interface MessageResponse {
  reply: string;
  campDay: number;
  stage: string;
  stageName: string;
  checkinDetected?: boolean;
  checkinTaskType?: string;
  checkinSummary?: string;
  completedCheckins?: string[];
  actions?: ChatAction[];
}

export interface ChatAction { label: string; value: string; tone?: "primary" | "secondary"; }
export interface ChatHistoryMessage { role: "assistant" | "user"; content: string; timestamp: string; actions?: ChatAction[]; }
export interface DesignPendingEdit { mode: "field" | "formula"; field?: string; label?: string; value?: string; formula?: Record<string, string>; }

export async function getAccessMode(): Promise<{ internalTestMode: boolean }> {
  const response = await fetch("/api/chat/access-mode");
  return response.json() as Promise<{ internalTestMode: boolean }>;
}

export async function enterInternalTest(testId: string, nickname: string, accessCode: string): Promise<{ chatUrl: string }> {
  const response = await fetch("/api/chat/internal-test/access", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ testId, nickname, accessCode }) });
  const payload: unknown = await response.json();
  if (!response.ok) throw new Error(readMessage(payload) ?? "无法进入内部测试");
  return payload as { chatUrl: string };
}

export async function logoutInternalTest(): Promise<void> {
  await fetch("/api/chat/internal-test/logout", { method: "POST" });
}

export interface CheckinResponse {
  feedback: string;
}

function getUserId(): string {
  const params = new URLSearchParams(window.location.search);
  return params.get("userId") || params.get("openid") || params.get("wxid") || "demo-user";
}

function getUserName(): string {
  const params = new URLSearchParams(window.location.search);
  return params.get("userName") || params.get("name") || "";
}

function getDay(): string {
  const params = new URLSearchParams(window.location.search);
  return params.get("day") || "";
}

function queryString(): string {
  const uid = getUserId();
  const uname = getUserName();
  const d = getDay();
  let qs = `userId=${encodeURIComponent(uid)}`;
  if (uname) qs += `&userName=${encodeURIComponent(uname)}`;
  if (d) qs += `&day=${encodeURIComponent(d)}`;
  return qs;
}

export async function openChat(): Promise<OpenResponse> {
  const response = await fetch(`/api/chat/open?${queryString()}`);
  const payload: unknown = await response.json();
  if (!response.ok) {
    throw new Error(readMessage(payload) ?? "Failed to open chat");
  }
  return payload as OpenResponse;
}

export async function sendMessage(message: string): Promise<MessageResponse> {
  // 不能让任何一次请求无限挂住，否则页面只会留下一个“正在输入”的空白气泡。
  // 程序按钮本身会在服务端绕过模型；这个超时仍作为网络与服务异常的最后保护。
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 45_000);
  try {
    const response = await fetch(`/api/chat/message?${queryString()}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message }),
      signal: controller.signal,
    });
    const payload: unknown = await response.json();
    if (!response.ok) {
      throw new Error(readMessage(payload) ?? "Failed to send message");
    }
    return payload as MessageResponse;
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new Error("小葵暂时没有回应，请检查网络后再试一次。");
    }
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
}

export async function fetchDesignPendingEdit(): Promise<DesignPendingEdit> {
  const response = await fetch(`/api/chat/design/pending-edit?${queryString()}`);
  const payload: unknown = await response.json();
  if (!response.ok) throw new Error(readMessage(payload) ?? "没有可编辑的内容");
  return payload as DesignPendingEdit;
}

export async function updateDesignPendingEdit(body: { value?: string; formula?: Record<string, string> }): Promise<MessageResponse> {
  const response = await fetch(`/api/chat/design/pending-edit?${queryString()}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  const payload: unknown = await response.json();
  if (!response.ok) throw new Error(readMessage(payload) ?? "更新失败");
  return payload as MessageResponse;
}

export async function submitCheckin(taskType: string, content: string): Promise<CheckinResponse> {
  const response = await fetch(`/api/chat/checkin?${queryString()}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ taskType, content }),
  });
  const payload: unknown = await response.json();
  if (!response.ok) {
    throw new Error(readMessage(payload) ?? "Failed to submit check-in");
  }
  return payload as CheckinResponse;
}

export async function registerFormula(formula: Record<string, string>): Promise<void> {
  const response = await fetch(`/api/chat/register-formula?${queryString()}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(formula),
  });
  if (!response.ok) {
    const payload: unknown = await response.json();
    throw new Error(readMessage(payload) ?? "Failed to register formula");
  }
}

export interface ProgressResponse {
  campDay: number;
  phase: { week: number; name: string; isRest: boolean };
  theme: string;
  habitFormula: Record<string,string> | null;
  habitFormulaDraft: (Record<string, string> & { confirmedFields: string[]; updatedAt: string }) | null;
  formulaVersions: Array<{ version: number; changed: string; timestamp: string }>;
  practiceRecords: Array<{ id: number; date: string; status: string; note: string }>;
  checkinDays: Array<{ day: number; entries: Array<{ id: number; taskType: string; content: string; confirmed: boolean; isRequired: boolean; isLesson: boolean }> }>;
  insights: Array<{ id: number; summary: string; day: number }>;
  dailySummaries: Array<{ id: number; day: number; summaryDate: string; taskSummary: string; discussionSummary: string }>;
}

export interface DailySummaryPreview {
  hasContent: boolean;
  message?: string;
  summary?: { day: number; summaryDate: string; taskSummary: string; discussionSummary: string };
}

async function progressRequest(path: string, method: "POST" | "PUT" | "DELETE", body?: unknown): Promise<void> {
  const init: RequestInit = { method };
  if (body !== undefined) { init.headers = { "Content-Type": "application/json" }; init.body = JSON.stringify(body); }
  const response = await fetch(`/api/chat/progress/${path}?${queryString()}`, init);
  const payload: unknown = await response.json();
  if (!response.ok) throw new Error(readMessage(payload) ?? "保存失败");
}
export const addProgressCheckin = (body: { taskType: string; content: string; day: number }) => progressRequest("checkins", "POST", body);
export const updateProgressCheckin = (id: number, body: { taskType: string; content: string; day: number }) => progressRequest(`checkins/${id}`, "PUT", body);
export const deleteProgressCheckin = (id: number) => progressRequest(`checkins/${id}`, "DELETE");
export const addProgressInsight = (body: { summary: string; day: number }) => progressRequest("insights", "POST", body);
export const updateProgressInsight = (id: number, body: { summary: string }) => progressRequest(`insights/${id}`, "PUT", body);
export const deleteProgressInsight = (id: number) => progressRequest(`insights/${id}`, "DELETE");
export const addProgressDailySummary = (body: { day: number; summaryDate: string; taskSummary: string; discussionSummary: string }) => progressRequest("daily-summaries", "POST", body);
export const updateProgressDailySummary = (id: number, body: { day: number; summaryDate: string; taskSummary: string; discussionSummary: string }) => progressRequest(`daily-summaries/${id}`, "PUT", body);
export const deleteProgressDailySummary = (id: number) => progressRequest(`daily-summaries/${id}`, "DELETE");
export const addProgressPractice = (body: { date: string; status: string; note: string }) => progressRequest("practice-records", "POST", body);
export const updateProgressPractice = (id: number, body: { date: string; status: string; note: string }) => progressRequest(`practice-records/${id}`, "PUT", body);
export const deleteProgressPractice = (id: number) => progressRequest(`practice-records/${id}`, "DELETE");
export const saveProgressDraft = (body: Record<string, string>) => progressRequest("draft", "PUT", body);
export const deleteProgressDraft = () => progressRequest("draft", "DELETE");
export const saveProgressFormula = (body: Record<string, string>) => progressRequest("formula", "PUT", body);
export const deleteProgressFormula = () => progressRequest("formula", "DELETE");

export async function fetchProgress(): Promise<ProgressResponse> {
  const response = await fetch(`/api/chat/progress?${queryString()}`);
  const payload: unknown = await response.json();
  if (!response.ok) {
    throw new Error(readMessage(payload) ?? "Failed to fetch progress");
  }
  return payload as ProgressResponse;
}

export async function generateDailySummary(): Promise<DailySummaryPreview> {
  const response = await fetch(`/api/chat/daily-summary?${queryString()}`, { method: "POST" });
  const payload: unknown = await response.json();
  if (!response.ok) throw new Error(readMessage(payload) ?? "生成今日总结失败");
  return payload as DailySummaryPreview;
}

function readMessage(value: unknown): string | undefined {
  if (typeof value !== "object" || value === null) return undefined;
  const record = value as Record<string, unknown>;
  return typeof record.message === "string" ? record.message : undefined;
}
